import {computed, type ComputedRef, type MaybeRefOrGetter, type Ref, toValue} from "vue";
import type {CellClickedEvent, GetContextMenuItemsParams, MenuItemDef} from "ag-grid-community";
import Translator from "@/utils/common/Translator.ts";
import {buildDrillDownClick, buildDrillDownByClick, executeLink} from "@/components/bi/biInteractionHelpers.ts";
import {useBIFilterBadges} from "@/components/bi/useBIFilterBadges.ts";
import {useBIInteractionData} from "@/components/bi/useBIInteractionData.ts";
import type {
  BIClickAction,
  BIDrillDownTarget,
  BIDrillDownByDimension,
  BILinkTarget,
  BIDrillDownClick,
  BIDrillDownByClick,
  CrossFilterEmission,
} from "@/components/bi/biChartTypes.ts";

interface InteractionEmits {
  crossFilterClicked: (emissions: CrossFilterEmission[]) => void;
  drillDownRequested: (click: BIDrillDownClick) => void;
  drillDownByRequested: (click: BIDrillDownByClick) => void;
}

interface UseBIInteractionsArgs {
  chartLine: Ref<any>;
  widgetId: ComputedRef<string | undefined>;
  dashboardId: MaybeRefOrGetter<string | undefined>;
  emit: InteractionEmits;
}

/**
 * Shared BI interaction logic for AG-Grid-based dashboard widgets (Table, EnhancedTable).
 * Wraps click-emit, drill-down, link, and drill-down-by behavior in a single composable so
 * the cell-click handler, context menu builder, and filter-badge tooltips are identical
 * across widgets that render via AG Grid.
 */
export function useBIInteractionsForAgGrid(args: UseBIInteractionsArgs) {
  const {chartLine, widgetId, dashboardId, emit} = args;

  const {
    interactionData, clickEmitData, drillTargets, drillFilterMetadata, drillPoints,
    linkTargets, linkPoints, drillByDimensions, drillByData,
  } = useBIInteractionData(chartLine);

  const appliedCrossFilterCodes = computed<string[]>(() => (chartLine.value as any)?.appliedCrossFilterCodes ?? []);
  const {isFilterSource, isFilteredByOther, filteredByTooltip, emittingTooltip} =
    useBIFilterBadges(widgetId, appliedCrossFilterCodes);

  function matchesCellColumn(entry: {column?: string}, cellField: string | undefined): boolean {
    return !!entry.column && entry.column === cellField;
  }
  function isRowLevel(entry: {column?: string}): boolean {
    return !entry.column;
  }

  // Cross-tab subtotal/grand-total rows and columns are derived aggregates with no underlying
  // source row, so click-emit / drill-down / link / drill-by all skip them. The renderer marks
  // them via _totalLevel on the row data and a "t" property on the synthetic column config.
  function isTotalRow(rowData: unknown): boolean {
    return rowData != null && typeof rowData === 'object' && '_totalLevel' in (rowData as object)
      && (rowData as {_totalLevel?: number})._totalLevel != null;
  }
  function isTotalColumn(colId: string | undefined): boolean {
    if (!colId) return false;
    const cols = ((chartLine.value as any)?.enhancedTableData?.columns ?? []) as {id: string; t?: number}[];
    const col = cols.find(c => c.id === colId);
    return !!col && col.t != null;
  }

  // Finds the first drillDown/link mapping marked onCellClick whose scope matches the clicked cell:
  // row-level (no column) or column matching the clicked column. Drill-down wins over link when both
  // would match (drill is typically the more specific action).
  function findCellClickTarget(cellField: string | undefined):
    {kind: 'drillDown'; target: BIDrillDownTarget} | {kind: 'link'; target: BILinkTarget} | undefined {
    for (const t of drillTargets.value)
      if (t.onCellClick && (matchesCellColumn(t, cellField) || isRowLevel(t)))
        return {kind: 'drillDown', target: t};
    for (const l of linkTargets.value)
      if (l.onCellClick && (matchesCellColumn(l, cellField) || isRowLevel(l)))
        return {kind: 'link', target: l};
    return undefined;
  }

  // Resolves a widget-level clickAction and fires it. Returns true when a link or drillDown action
  // was dispatched, false when caller should fall through to the default (cross-filter emit).
  // The resolved target's `column` scope is respected — a target with column="X" only fires when
  // the clicked cell is in column X; row-level targets (no column) fire on any cell.
  function dispatchWidgetClickAction(action: BIClickAction | undefined, rowIdx: number,
                                     cellField: string | undefined): boolean {
    if (!action || !action.targetKey || action.type === 'crossFilter') return false;

    if (action.type === 'link') {
      const link = linkTargets.value.find(l => l.key === action.targetKey);
      if (!link) return false;
      if (link.column && !matchesCellColumn(link, cellField)) return false;
      const pv = linkPoints.value[rowIdx]?.[link.key];
      if (!pv) return false;
      executeLink(link, pv);
      return true;
    }

    if (action.type === 'drillDown') {
      const target = drillTargets.value.find(t => t.key === action.targetKey);
      if (!target) return false;
      if (target.column && !matchesCellColumn(target, cellField)) return false;
      triggerDrillDown(target, rowIdx);
      return true;
    }

    return false;
  }

  function onCellClicked(params: CellClickedEvent) {
    const rowIdx = params.node?.rowIndex;
    if (rowIdx == null) return;
    if (isTotalRow(params.node?.data)) return;
    const cellField = params.column?.getColId();
    if (isTotalColumn(cellField)) return;

    const cellTarget = findCellClickTarget(cellField);
    if (cellTarget) {
      if (cellTarget.kind === 'drillDown') triggerDrillDown(cellTarget.target, rowIdx);
      else triggerLink(cellTarget.target, rowIdx);
      return;
    }

    if (dispatchWidgetClickAction(interactionData.value?.clickAction, rowIdx, cellField)) return;

    if (!clickEmitData.value.length) return;

    const cellMatches = clickEmitData.value.filter(m => matchesCellColumn(m, cellField));
    const selected = cellMatches.length ? cellMatches : clickEmitData.value.filter(isRowLevel);
    if (!selected.length) return;

    const emissions: CrossFilterEmission[] = [];
    for (const mapping of selected) {
      const point = mapping.points?.[rowIdx];
      if (point && Object.keys(point).length > 0) {
        emissions.push({
          crossFilterCode: mapping.crossFilterCode,
          sourceChartId: mapping.sourceChartId ?? widgetId.value,
          dataIndex: rowIdx,
          ...point,
        });
      }
    }
    if (emissions.length) emit.crossFilterClicked(emissions);
  }

  function triggerDrillDown(target: BIDrillDownTarget, rowIdx: number) {
    const sourceWidgetCode = (chartLine.value as any)?.widgetCode;
    const pointValues = drillPoints.value[rowIdx];
    if (!pointValues) return;
    const click = buildDrillDownClick(target, pointValues, drillFilterMetadata.value,
      sourceWidgetCode, widgetId.value);
    if (click) emit.drillDownRequested(click);
  }

  function triggerLink(link: BILinkTarget, rowIdx: number) {
    const pv = linkPoints.value[rowIdx]?.[link.key];
    if (!pv) return;
    executeLink(link, pv);
  }

  function triggerDrillDownBy(dim: BIDrillDownByDimension, rowIdx: number) {
    const sourceWidgetCode = (chartLine.value as any)?.widgetCode;
    const click = buildDrillDownByClick(dim, drillByData.value, rowIdx,
      sourceWidgetCode, widgetId.value, toValue(dashboardId));
    if (click) emit.drillDownByRequested(click);
  }

  function drillDownLabel(t: BIDrillDownTarget): string {
    const ar = Translator.isArabic();
    return (ar ? t.arTitle : t.enTitle) || (ar ? t.enTitle : t.arTitle) || Translator.translate('biDrillDown');
  }
  function linkLabel(l: BILinkTarget): string {
    const ar = Translator.isArabic();
    return (ar ? l.arLabel : l.enLabel) || (ar ? l.enLabel : l.arLabel) || l.key;
  }
  function drillByLabel(d: BIDrillDownByDimension): string {
    const ar = Translator.isArabic();
    return (ar ? d.arTitle : d.enTitle) || (ar ? d.enTitle : d.arTitle) || d.fieldId;
  }

  function getContextMenuItems(params: GetContextMenuItemsParams): (string | MenuItemDef)[] {
    const defaults = (params.defaultItems ?? []).slice() as (string | MenuItemDef)[];
    if (!hasInteraction.value) return defaults;
    const rowIdx = params.node?.rowIndex;
    if (rowIdx == null) return defaults;
    if (isTotalRow(params.node?.data)) return defaults;
    const cellField = params.column?.getColId();
    if (isTotalColumn(cellField)) return defaults;

    const biItems: (string | MenuItemDef)[] = [];
    const mergedDrills = [
      ...drillTargets.value.filter(isRowLevel),
      ...drillTargets.value.filter(t => matchesCellColumn(t, cellField)),
    ];
    for (const target of mergedDrills) {
      biItems.push({
        name: drillDownLabel(target),
        action: () => triggerDrillDown(target, rowIdx),
      });
    }

    const mergedLinks = [
      ...linkTargets.value.filter(isRowLevel),
      ...linkTargets.value.filter(l => matchesCellColumn(l, cellField)),
    ];
    if (mergedLinks.length && biItems.length) biItems.push('separator');
    for (const link of mergedLinks) {
      biItems.push({
        name: linkLabel(link),
        action: () => triggerLink(link, rowIdx),
      });
    }

    if (drillByDimensions.value.length) {
      if (biItems.length) biItems.push('separator');
      biItems.push({
        name: Translator.translate('biDrillDownBy'),
        subMenu: drillByDimensions.value.map<MenuItemDef>(dim => ({
          name: drillByLabel(dim),
          action: () => triggerDrillDownBy(dim, rowIdx),
        })),
      });
    }

    if (!biItems.length) return defaults;
    return [...defaults, 'separator', ...biItems];
  }

  const hasInteraction = computed(
    () => clickEmitData.value.length > 0 || drillTargets.value.length > 0 || linkTargets.value.length > 0
      || drillByDimensions.value.length > 0,
  );

  const hasRowLevelCellClickTarget = computed(() =>
    drillTargets.value.some(t => t.onCellClick && isRowLevel(t)) ||
    linkTargets.value.some(l => l.onCellClick && isRowLevel(l)),
  );

  // Looks up the target that a widget-level clickAction points to, so cursor hints can honor the
  // target's column scope. Returns undefined when clickAction is absent, cross-filter, or
  // references a missing key.
  const widgetActionTarget = computed<{column?: string} | undefined>(() => {
    const a = interactionData.value?.clickAction;
    if (!a?.targetKey || a.type === 'crossFilter') return undefined;
    if (a.type === 'link') return linkTargets.value.find(l => l.key === a.targetKey);
    if (a.type === 'drillDown') return drillTargets.value.find(t => t.key === a.targetKey);
    return undefined;
  });

  const clickableCellCols = computed<Set<string>>(() => {
    const cols = new Set<string>();
    const anyRowLevelEmit = clickEmitData.value.some(isRowLevel);
    if (!anyRowLevelEmit) {
      for (const m of clickEmitData.value)
        if (m.column) cols.add(m.column);
    }
    for (const t of drillTargets.value)
      if (t.onCellClick && t.column) cols.add(t.column);
    for (const l of linkTargets.value)
      if (l.onCellClick && l.column) cols.add(l.column);
    const widgetTargetCol = widgetActionTarget.value?.column;
    if (widgetTargetCol) cols.add(widgetTargetCol);
    return cols;
  });

  function cellClassForInteraction(params: {colDef: {colId?: string; field?: string}; data?: unknown}): string | undefined {
    const colId = params.colDef.colId ?? params.colDef.field;
    if (!colId) return undefined;
    if (isTotalRow(params.data) || isTotalColumn(colId)) return undefined;
    const hasEmit = clickEmitData.value.length > 0;
    const anyRowLevelEmit = hasEmit && clickEmitData.value.some(isRowLevel);
    const widgetTarget = widgetActionTarget.value;
    const widgetActionRowLevel = !!widgetTarget && !widgetTarget.column;
    if (anyRowLevelEmit || widgetActionRowLevel || hasRowLevelCellClickTarget.value
        || clickableCellCols.value.has(colId))
      return 'bi-clickable-cell';
    return undefined;
  }

  return {
    interactionData,
    hasInteraction,
    isFilterSource,
    isFilteredByOther,
    filteredByTooltip,
    emittingTooltip,
    onCellClicked,
    getContextMenuItems,
    cellClassForInteraction,
  };
}
